import { useCallback, useEffect, useMemo, useState } from 'react';

import Dropdown, { type DropdownProps } from '@/components/Dropdown/Dropdown';
import type { ActionMenuT } from '@/components/Menu/MenuItem';
import { UNASSIGNED_LABEL } from '@/configs/reserved-word';
import ActionModal from '@/features/Commons/DataTableCustomComponents/CheckedFeedbackActions/ActionModal/ActionModal';
import CollectiveAgentSelectDropdownMenu from '@/features/Commons/DataTableCustomComponents/CheckedFeedbackActions/CollectiveAgentSelectDropdownMenu';
import CollectiveTagSelectModal from '@/features/Commons/DataTableCustomComponents/CheckedFeedbackActions/CollectiveTagSelectModal';
import DataOriginSelectModal from '@/features/Feedback/field/DataOrigin/DataOriginDropdown/DataOriginSelectModal';
import useStates from '@/hooks/pure/useStates';
import useFeedbackListStore from '@/stores/Feedback/FeedbackListStore';
import useDetailModalStore from '@/stores/pure/DetailModalStore';
import useDropdownStore from '@/stores/pure/DropdownStore';
import usePopupStore from '@/stores/pure/PopupStore';
import useToastStore from '@/stores/pure/ToastStore';

export type FeedbackActionModalProps = {
  tableKey: string;
  feedbackAliases: string[];
  dropdownProps: DropdownProps;
  clearCheckList: () => void;
  onDeleteSuccess: (feedbackCount: number) => void;
};

const TAG_SELECT_MODAL_KEY = 'collective-tag-select-modal';
const AGENT_SELECT_MODAL_KEY = 'collective-agent-select-modal';
const DATA_ORIGIN_SELECT_MODAL_KEY = 'collective-data-origin-select-modal';
export const CHAT_GPT_MODAL_KEY = 'chat-gpt-modal';

const FeedbackActionModal = ({
  dropdownProps,
  ...props
}: FeedbackActionModalProps) => {
  const { setPopup } = usePopupStore();
  const { initModal } = useDetailModalStore();
  const { addDefaultToast } = useToastStore();
  const setCurrDropdown = useDropdownStore((state) => state.setCurrDropdown);
  const currDropdown = useDropdownStore((state) => state.currDropdown);

  const {
    feedbackMap,
    addTagsToFeedbacks,
    removeTagsFromFeedbacks,
    changeAgentOfFeedbacks,
    removeFeedbacks,
    setState,
    changeDataOriginOfFeedbacks,
  } = useFeedbackListStore((state) => ({
    feedbackMap: state.feedbackMap,
    changeDataOriginOfFeedbacks: state.changeDataOriginOfFeedbacks,
    addTagsToFeedbacks: state.addTagsToFeedbacks,
    removeTagsFromFeedbacks: state.removeTagsFromFeedbacks,
    changeAgentOfFeedbacks: state.changeAgentOfFeedbacks,
    removeFeedbacks: state.removeFeedbacks,
    setState: state.setState,
  }));

  const [childModalEl, setChildModalEl] = useState<HTMLElement>();

  type State = {
    origSelectedTags: string[];
    currSelectedTags: string[];
    currSelectedAgent: string;
  };

  const { state, setValue } = useStates<State>({
    origSelectedTags: [],
    currSelectedTags: [],
    // origSelectedTags and currSelectedTags is set to be
    // the intersection of the tags taken by the list of checked feedback
    currSelectedAgent: UNASSIGNED_LABEL,
    // currSelectedAgent is set to be either an actual agent alias or UNASSIGNED_LABEL
  });

  useEffect(() => {
    const tagCountMap: { [key: string]: number } = {}; // { tagAlias: count }
    const agentCountMap: { [key: string]: number } = {}; // { tagAlias: count }
    props.feedbackAliases.map((fa) => {
      const feedback = feedbackMap.get(fa);
      if (feedback && feedback.labels) {
        feedback.labels.map((ta) => {
          if (!tagCountMap[ta]) tagCountMap[ta] = 0;
          tagCountMap[ta] += 1;
        });
      }
      if (feedback && feedback.manager) {
        const aa = feedback.manager;
        if (!agentCountMap[aa]) agentCountMap[aa] = 0;
        agentCountMap[aa] += 1;
      }
    });

    const numChecks = props.feedbackAliases.length;
    const selectedTags: string[] = [];
    let selectedAgent: string = UNASSIGNED_LABEL;
    Object.entries(tagCountMap).map(([ta, count]) => {
      if (count === numChecks) selectedTags.push(ta);
    });
    Object.entries(agentCountMap).map(([aa, count]) => {
      if (count === numChecks) selectedAgent = aa;
    });

    setValue('origSelectedTags', selectedTags);
    setValue('currSelectedTags', selectedTags);
    setValue('currSelectedAgent', selectedAgent);
  }, [props.feedbackAliases, feedbackMap, setValue]);

  const toggleTag = useCallback(
    (tagAlias: string) => {
      if (state.currSelectedTags.includes(tagAlias))
        setValue(
          'currSelectedTags',
          state.currSelectedTags.filter((ta) => ta !== tagAlias),
        );
      else setValue('currSelectedTags', [...state.currSelectedTags, tagAlias]);
    },
    [state.currSelectedTags, setValue],
  );

  const updateTags = async () => {
    initModal();
    setState({ listLoading: true });
    const count = props.feedbackAliases.length;

    try {
      // Add currSelectedTags to the list of checked feedback
      addTagsToFeedbacks(props.feedbackAliases, state.currSelectedTags);
      // Remove (origSelectedTags - currSelectedTags) from the list of checked feedback
      removeTagsFromFeedbacks(
        props.feedbackAliases,
        state.origSelectedTags.filter(
          (ta) => !state.currSelectedTags.includes(ta),
        ),
      );
      addDefaultToast(`Tags changed for ${count} feedback`, 'success');
    } catch (e) {
      addDefaultToast('Changing tag failed', 'fail');
    } finally {
      setState({ listLoading: false });
      dropdownProps.close?.();
    }
  };

  const handleSelectAgent = (agentAlias: string) => {
    setValue('currSelectedAgent', agentAlias);
  };

  const updateAgents = async () => {
    initModal();
    setState({ listLoading: true });
    const count = props.feedbackAliases.length;
    let agentAlias: string | null = state.currSelectedAgent;
    if (agentAlias === UNASSIGNED_LABEL) agentAlias = null;

    try {
      // Apply currSelectedAgent to the list of checked feedback
      await changeAgentOfFeedbacks(props.feedbackAliases, agentAlias);
      addDefaultToast(`Agent changed for ${count} feedback`, 'success');
    } catch (e) {
      addDefaultToast('Changing agent failed', 'fail');
    } finally {
      props.clearCheckList();
      setState({ listLoading: false });
      dropdownProps.close?.();
    }
  };

  const setChildModal = useCallback(
    (el: HTMLElement, modalKey: string) => {
      setChildModalEl(el);
      setCurrDropdown(modalKey);
    },
    [setCurrDropdown],
  );

  const updateDataOrigin = (key: string) => {
    changeDataOriginOfFeedbacks(props.feedbackAliases, key);
    dropdownProps.close?.();
  };

  const clearDataOrigin = () => {
    changeDataOriginOfFeedbacks(props.feedbackAliases, null);
    dropdownProps.close?.();
  };

  const onDeleteCallback = useCallback(() => {
    setPopup({
      popupTheme: 'negative',
      type: 'confirm',
      title: 'Delete feedback',
      message:
        'Are you sure you want to delete the selected feedback? This cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        initModal();
        setState({ listLoading: true });
        const feedbackCount = props.feedbackAliases.length;
        try {
          await removeFeedbacks(props.feedbackAliases);
          addDefaultToast(`${feedbackCount} Feedback Deleted`, 'success');
          props.onDeleteSuccess(feedbackCount);
        } catch (e) {
          addDefaultToast(`Deleting Feedback Failed`, 'fail');
        } finally {
          props.clearCheckList();
          setState({ listLoading: false });
          dropdownProps.close?.();
        }
      },
    });
  }, [
    addDefaultToast,
    dropdownProps,
    initModal,
    props.feedbackAliases,
    props.clearCheckList,
    removeFeedbacks,
    setPopup,
    setState,
  ]);

  const actionMenus = useMemo(() => {
    const menus: ActionMenuT[] = [
      {
        id: 'tag',
        node: 'Tag',
        shortcut: {
          keyCode: 'KeyL',
          keyName: 'L',
          callback: (el) => setChildModal(el, TAG_SELECT_MODAL_KEY),
        },
        hasSubMenu: true,
        onClick: (e) =>
          setChildModal(e.currentTarget as HTMLElement, TAG_SELECT_MODAL_KEY),
      },
      {
        id: 'data_origin',
        node: 'Data origin',
        shortcut: {
          keyCode: 'KeyD',
          keyName: 'D',
          callback: (el) => setChildModal(el, DATA_ORIGIN_SELECT_MODAL_KEY),
        },
        hasSubMenu: true,
        onClick: (e) =>
          setChildModal(
            e.currentTarget as HTMLElement,
            DATA_ORIGIN_SELECT_MODAL_KEY,
          ),
      },
      {
        id: 'agent',
        node: 'Agent',
        shortcut: {
          keyCode: 'KeyA',
          keyName: 'A',
          callback: (el) => setChildModal(el, AGENT_SELECT_MODAL_KEY),
        },
        hasSubMenu: true,
        onClick: (e) =>
          setChildModal(e.currentTarget as HTMLElement, AGENT_SELECT_MODAL_KEY),
      },
      {
        id: 'delete',
        node: 'Delete',
        shortcut: {
          keyCode: 'Backspace',
          keyName: 'Backspace',
          callback: () => onDeleteCallback(),
        },
        onClick: () => {
          onDeleteCallback();
        },
      },
    ];

    return menus;
  }, [setChildModal, onDeleteCallback]);

  return (
    <Dropdown
      status={dropdownProps.status}
      close={dropdownProps.close}
      triggerPos={dropdownProps.triggerPos}
      modalWidth="180px"
    >
      <ActionModal menus={actionMenus} targets={props.feedbackAliases} />
      <CollectiveTagSelectModal
        status={currDropdown === TAG_SELECT_MODAL_KEY}
        selectedTags={state.currSelectedTags}
        toggleItem={toggleTag}
        updateTags={updateTags}
        closeModal={() => setCurrDropdown(null)}
        triggerEl={childModalEl as HTMLDivElement}
        modalOptions={{
          direction: 'right',
          offset: {
            x: 14,
          },
        }}
        modalWidth="400px"
      />
      <CollectiveAgentSelectDropdownMenu
        selectedAgent={state.currSelectedAgent}
        onOptionSelect={handleSelectAgent}
        updateAgents={updateAgents}
        dropdownProps={{
          triggerEl: childModalEl,
          modalOptions: {
            direction: 'right',
            offset: {
              x: 14,
            },
          },
          status: currDropdown === AGENT_SELECT_MODAL_KEY,
          close: () => setCurrDropdown(null),
          modalWidth: '300px',
        }}
      />
      <DataOriginSelectModal
        selected={null}
        name={DATA_ORIGIN_SELECT_MODAL_KEY}
        onSelect={updateDataOrigin}
        onClear={clearDataOrigin}
        triggerEl={childModalEl as HTMLDivElement}
        modalOptions={{
          direction: 'right',
          offset: {
            x: 14,
          },
        }}
      />
    </Dropdown>
  );
};

export default FeedbackActionModal;
